import { useMutation, useQuery } from "@apollo/client";
import Azure from "@static/integration-logos/Azure.svg";
import BambooHR from "@static/integration-logos/BambooHR.svg";
import Breathe from "@static/integration-logos/Breathe.svg";
import Fourth from "@static/integration-logos/Fourth.svg";
import Google from "@static/integration-logos/Google.svg";
import Hibob from "@static/integration-logos/Hibob.svg";
import JumpCloud from "@static/integration-logos/JumpCloud.svg";
import Okta from "@static/integration-logos/Okta.svg";
import OneLogin from "@static/integration-logos/OneLogin.svg";
import PingFederate from "@static/integration-logos/PingFederate.svg";
import Rippling from "@static/integration-logos/Rippling.svg";
import SCIM from "@static/integration-logos/SCIM.svg";
import Workday from "@static/integration-logos/Workday.svg";
import moment from "moment/moment";
import { useEffect, useState } from "react";
import {
  GetOrganizationSettingsQuery,
  GetOrganizationSettingsQueryVariables,
  OrgSettingsOrganizationFragmentFragment,
  SyncWithHrisMutationMutation,
  SyncWithHrisMutationMutationVariables,
} from "types/graphql-schema";

import getOrganizationSettingsQuery from "@apps/org-settings/graphql/get-organization-settings-query";
import Button from "@components/button/button";
import Heading from "@components/heading/heading";
import { assertEdgesNonNull, getAssetUrl } from "@helpers/helpers";

import syncWithHrisMutation from "../graphql/sync-with-hris-mutation";

const DirectorySync = ({
  organization,
}: {
  organization: OrgSettingsOrganizationFragmentFragment;
}) => {
  const [syncWithHris, { loading }] = useMutation<
    SyncWithHrisMutationMutation,
    SyncWithHrisMutationMutationVariables
  >(syncWithHrisMutation);

  const [processing, setProcessing] = useState(false);
  const [lastSyncEnd, setLastSyncEnd] = useState<string | null | undefined>(
    organization.directorySync?.lastSyncEnd
  );

  const handleSyncClick = () => {
    syncWithHris({
      variables: { organizationId: organization.id },
      onCompleted: () => {
        setProcessing(true);
      },
    });
  };

  const { data } = useQuery<
    GetOrganizationSettingsQuery,
    GetOrganizationSettingsQueryVariables
  >(getOrganizationSettingsQuery);

  useEffect(() => {
    const organizations = assertEdgesNonNull(data?.me?.organizations);
    const newLastSyncEnd = organizations
      ? organizations[0].directorySync?.lastSyncEnd
      : null;
    const isDone = lastSyncEnd !== newLastSyncEnd;

    if (processing && isDone) {
      setLastSyncEnd(newLastSyncEnd);
      setProcessing(false);
    }
  }, [processing, data, lastSyncEnd]);

  return (
    <div>
      <Heading small title="HRIS Sync" />

      {!!organization.directorySync ? (
        <div>
          <ul className="mt-6 list-disc ml-4">
            <li>
              <span className="font-medium">Name:</span>{" "}
              {organization.directorySync?.name}
            </li>
            <li>
              <span className="font-medium">State:</span>{" "}
              {organization.directorySync?.state}
            </li>
            <li>
              <span className="font-medium">Type:</span>{" "}
              {organization.directorySync?.type}
            </li>
            <li>
              <span className="font-medium">Last synced at:</span>{" "}
              {organization.directorySync?.lastSyncEnd
                ? moment(organization.directorySync?.lastSyncEnd).format(
                    "MMM D, YYYY h:mmA"
                  )
                : "Never"}
            </li>
          </ul>
          {organization.directorySync.type === "Rippling" && (
            <Button
              text={"Sync Now"}
              onClick={handleSyncClick}
              disabled={loading || processing}
            />
          )}
        </div>
      ) : (
        <div className="mt-6">
          <a
            href="https://www.topicflow.com/contact-us/"
            className="text-blue-500 hover:underline"
          >
            Contact us
          </a>{" "}
          to sync your organization's HRIS.
        </div>
      )}

      <div className="mt-10">
        <div className="pb-12 text-center mx-auto grid grid-cols-2 auto-cols-[1fr] max-w-md lg:block lg:max-w-none">
          <div className="inline-block mt-8 mx-auto lg:mx-8">
            <img src={getAssetUrl(Azure)} loading="lazy" alt="" />
          </div>
          <div className="inline-block mt-8 mx-auto lg:mx-8">
            <img src={getAssetUrl(Okta)} loading="lazy" alt="" />
          </div>
          <div className="inline-block mt-8 mx-auto lg:mx-8">
            <img src={getAssetUrl(Google)} loading="lazy" alt="" />
          </div>
          <div className="inline-block mt-8 mx-auto lg:mx-8">
            <img src={getAssetUrl(BambooHR)} loading="lazy" alt="" />
          </div>
          <div className="inline-block mt-8 mx-auto lg:mx-8">
            <img src={getAssetUrl(Hibob)} loading="lazy" alt="" />
          </div>
          <div className="inline-block mt-8 mx-auto lg:mx-8">
            <img src={getAssetUrl(Workday)} loading="lazy" alt="" />
          </div>
          <div className="inline-block mt-8 mx-auto lg:mx-8">
            <img src={getAssetUrl(OneLogin)} loading="lazy" alt="" />
          </div>
          <div className="inline-block mt-8 mx-auto lg:mx-8">
            <img src={getAssetUrl(JumpCloud)} loading="lazy" alt="" />
          </div>
          <div className="inline-block mt-8 mx-auto lg:mx-8">
            <img src={getAssetUrl(SCIM)} loading="lazy" alt="" />
          </div>
          <div className="inline-block mt-8 mx-auto lg:mx-8">
            <img src={getAssetUrl(Fourth)} loading="lazy" alt="" />
          </div>
          <div className="inline-block mt-8 mx-auto lg:mx-8">
            <img src={getAssetUrl(Rippling)} loading="lazy" alt="" />
          </div>
          <div className="inline-block mt-8 mx-auto lg:mx-8">
            <img src={getAssetUrl(PingFederate)} loading="lazy" alt="" />
          </div>
          <div className="inline-block mt-8 mx-auto lg:mx-8">
            <img src={getAssetUrl(Breathe)} loading="lazy" alt="" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectorySync;
